import type { ReceiptStatus } from "@opensea/graphql"
import { proxy } from "valtio"
import { deepClone } from "valtio/utils"
import type { FlowStateVariants } from "../flow/flow-state"
import type { TransactionIdentifier } from "../stores/currentTransactionStore"
import { CurrentTransactionActions } from "../stores/currentTransactionStore"
import type { ItemQuoteInput } from "../types"
import { ErrorActions } from "./errorStore"
import { FlowCallbackActions } from "./flowCallbackStore"

// -- Types ---------------------------------------------------------
export type QuoteItem = ItemQuoteInput & {
  orderId?: string
}

type FlowStateType = {
  succeeded: boolean
  receiptTransactions: TransactionIdentifier[]
  itemQuotes: QuoteItem[]
  itemImageMap: Record<string, string | null>
  allActionsCompleted: boolean
  flow?: FlowStateVariants
  receiptStatus?: ReceiptStatus
  flowId?: string
}

// -- State ---------------------------------------------------------
const INITIAL_STATE: FlowStateType = {
  flow: undefined,
  succeeded: false,
  receiptTransactions: [],
  itemQuotes: [],
  itemImageMap: {},
  allActionsCompleted: false,
  receiptStatus: undefined,
} as const

export const FlowState = proxy<FlowStateType>(deepClone(INITIAL_STATE))

// -- Actions -------------------------------------------------------
export const FlowActions = {
  reset() {
    Object.assign(FlowState, INITIAL_STATE)
    FlowState.receiptTransactions = []
    FlowState.itemQuotes = []
    FlowState.itemImageMap = {}
    FlowState.flowId = undefined
  },

  startFlow(flow: FlowStateVariants) {
    FlowActions.reset()
    FlowState.flowId = String(Date.now())
    FlowState.flow = flow
    FlowCallbackActions.startAll(flow)
    ErrorActions.clear()
  },

  endFlow() {
    const { flow } = FlowState
    if (flow) {
      FlowCallbackActions.endAll(flow)
    }
    FlowActions.reset()
    CurrentTransactionActions.setTransaction(undefined)
  },

  onSuccess(flow?: FlowStateVariants) {
    const flowToUse = flow ?? FlowState.flow
    if (flowToUse) {
      FlowCallbackActions.succeedAll(flowToUse)
    }
  },

  setFlowField<
    TFlowState extends FlowStateVariants,
    TField extends keyof TFlowState = keyof TFlowState,
  >(field: TField, value: TFlowState[TField]) {
    if (FlowState.flow) {
      Object.assign(FlowState.flow, { [field]: value })
    }
  },

  setReceiptStatus(status: ReceiptStatus) {
    FlowState.receiptStatus = status
    if (status === "SUCCESS") {
      FlowState.succeeded = true
    }
  },

  addReceiptTransaction(transaction: TransactionIdentifier) {
    FlowState.receiptTransactions.push(transaction)
  },

  setItemQuotes(items: ItemQuoteInput[]) {
    FlowState.itemQuotes = items
  },

  setItemImageMap(itemImageMap: Record<string, string | null>) {
    FlowState.itemImageMap = itemImageMap
  },

  setAllActionsCompleted() {
    FlowState.allActionsCompleted = true
  },
}
